import {
  inferDriveFolderMatch,
  makeDriveFolderNode,
  normalizeTokenFolderName,
  type DriveFolderNode,
  type DriveFolderRecipe,
} from "./driveFolderRecipeModel.js";

type Segment = { kind: "atom"; name: string } | { kind: "switch"; alts: Segment[][] };

function skipSpace(s: string, i: number): number {
  while (i < s.length && /\s/.test(s[i]!)) i++;
  return i;
}

function matchingBrace(s: string, open: number): number {
  let depth = 0;
  for (let i = open; i < s.length; i++) {
    if (s[i] === "{") depth++;
    else if (s[i] === "}") {
      depth--;
      if (depth === 0) return i;
    }
  }
  return -1;
}

function innerIsGroup(inner: string): boolean {
  let depth = 0;
  for (let i = 0; i < inner.length; i++) {
    if (inner[i] === "{") depth++;
    else if (inner[i] === "}") depth = Math.max(0, depth - 1);
    else if (depth === 0 && (inner[i] === "," || inner[i] === "/")) return true;
  }
  return false;
}

function parseAtom(s: string, i: number): { name: string; i: number } {
  const start = i;
  while (i < s.length) {
    const ch = s[i]!;
    if (ch === "/" || ch === "," || ch === "}") break;
    if (ch === "{") {
      const end = matchingBrace(s, i);
      if (end < 0) break;
      const inner = s.slice(i + 1, end);
      if (innerIsGroup(inner)) break;
      i = end + 1;
      continue;
    }
    i++;
  }
  return { name: s.slice(start, i).trim(), i };
}

function parseSegmentList(s: string, i: number): { segments: Segment[]; i: number } {
  const segments: Segment[] = [];
  while (i < s.length) {
    i = skipSpace(s, i);
    const ch = s[i];
    if (!ch || ch === "," || ch === "}") break;
    if (ch === "/") {
      i++;
      continue;
    }
    if (ch === "{") {
      const end = matchingBrace(s, i);
      if (end < 0) break;
      const inner = s.slice(i + 1, end);
      if (innerIsGroup(inner)) {
        const group = parseGroup(s, i);
        segments.push({ kind: "switch", alts: group.alts });
        i = group.i;
        continue;
      }
    }
    const atom = parseAtom(s, i);
    if (!atom.name) break;
    segments.push({ kind: "atom", name: atom.name });
    i = atom.i;
  }
  return { segments, i };
}

function parseGroup(s: string, i: number): { alts: Segment[][]; i: number } {
  const alts: Segment[][] = [];
  i++;
  while (i < s.length) {
    i = skipSpace(s, i);
    if (s[i] === "}") {
      i++;
      break;
    }
    const part = parseSegmentList(s, i);
    if (part.segments.length) alts.push(part.segments);
    i = skipSpace(s, part.i);
    if (s[i] === ",") {
      i++;
      continue;
    }
    if (s[i] === "}") {
      i++;
      break;
    }
    break;
  }
  return { alts, i };
}

function wrapSegments(segments: Segment[]): DriveFolderNode[] {
  if (!segments.length) return [];
  const [head, ...rest] = segments;
  const inner = wrapSegments(rest);
  if (!head) return inner;
  if (head.kind === "atom") {
    const name = normalizeTokenFolderName(head.name);
    return [makeDriveFolderNode(name, inferDriveFolderMatch(name), inner)];
  }
  const cases = head.alts.flatMap((alt) => wrapSegments(alt));
  return [...cases, ...inner];
}

export function parseDriveFolderRecipeCompact(raw: string): DriveFolderRecipe | null {
  const s = raw.trim();
  if (!s) return null;
  const parsed = parseSegmentList(s, 0);
  const rest = s.slice(parsed.i).trim();
  if (rest) return null;
  if (!parsed.segments.length) return null;
  return { version: 1, nodes: wrapSegments(parsed.segments) };
}

function printNode(node: DriveFolderNode): string {
  const body = printDriveFolderNodesCompact(node.children);
  return body ? `${node.name}/${body}` : node.name;
}

export function printDriveFolderNodesCompact(nodes: DriveFolderNode[]): string {
  const cases = nodes.filter((n) => n.matchKey);
  const always = nodes.filter((n) => !n.matchKey);
  const parts: string[] = [];
  if (cases.length) parts.push(`{${cases.map(printNode).join(",")}}`);
  for (const n of always) parts.push(printNode(n));
  return parts.join("/");
}

export function printDriveFolderRecipeCompact(recipe: DriveFolderRecipe): string {
  return printDriveFolderNodesCompact(recipe.nodes);
}
